import { gql, useMutation } from '@apollo/client';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import type { MouseEvent, ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  FormProvider,
  useForm,
  useWatch,
} from 'react-hook-form';

import { SEARCHABLE_ORGANIZATION_SELECT_NEW_ORGANIZATION_PLACEHOLDER_ID } from '@/components/SearchableOrganizationSelect';
import {
  CreateUserV2Input,
  EUserRole,
} from '@/graphql-types/globalTypes';
import { logError } from '@/imports/logging/ClientLogger';
import { REG_EXP } from '@/utilities';

import {
  AccountForm,
  AccountFormValues,
} from '@/components/admin/membership/users/NewUser/components/AccountForm/AccountForm';
import {
  PersonalDetailsForm,
  PersonalDetailsFormValues,
} from '@/components/admin/membership/users/NewUser/components/PersonalDetailsForm/PersonalDetailsForm';
import { CreateCompanyForm } from '../sponsorships/Sponsors/components/Company/components/CreateCompanyForm';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { EditIcon } from '@/imports/ui/chakra/feather';

const CREATE_USER = gql`
  mutation NewAddContactModalMember_CreateUser($input: CreateUserV2Input!) {
    createUserV2(input: $input) {
      ... on CreateUserV2Success {
        user {
          _id
          displayName
        }
      }
      ... on CreateUserV2InvalidInputError {
        message
      }
      ... on CreateUserV2DuplicateEmailError {
        message
      }
    }
  }
`;

export type AddContactModalValues = PersonalDetailsFormValues &
  AccountFormValues & {
    contactType: 'Individual' | 'Organization';
  };

export type AddContactModalAction = 'save' | 'continue' | 'nonMember' | 'member';

export type AddContactModalProps = {
  isOpen: boolean;
  onClose: () => void;
  showStepper?: boolean;
  flow?: 'memberManagement' | 'newUser';
};

export const NewAddContactModalMember: React.FC<AddContactModalProps> = ({
  isOpen,
  onClose,
  showStepper = false,
  flow = 'memberManagement',
}) => {
  const toast = useToast();
  const router = useRouter();

  const defaultValues = useMemo<AddContactModalValues>(
    () => ({
      contactType: 'Individual',

      firstName: '',
      lastName: '',
      contactEmail: '',
      primaryPhone: '',
      jobTitle: '',
      company: '',
      addressLine0: '',
      addressLocality: '',
      addressAdministrativeArea: '',
      addressRegionCode: '',
      addressPostalCode: '',

      accountEmail: '',
      shouldSendInvitationEmail: true,
      role: EUserRole.REGULAR_USER,
      organization: undefined,
      isRecordOnly: false,
    }),
    []
  );

  const methods = useForm<AddContactModalValues>({
    defaultValues,
    mode: 'onChange' as const,
    reValidateMode: 'onChange' as const,
  });

  // Add this component above AddContactModal

const ReviewField = ({
  label,
  value,
}: {
  label: string;
  value?: ReactNode;
}) => (
  <Box>
    <Text fontSize="xs" fontWeight="semibold" color="gray.700">
      {label}
    </Text>
    <Text fontSize="sm" mt="1">
      {value || '-'}
    </Text>
  </Box>
);

const ReviewDetails = () => {
  const values = useWatch<AddContactModalValues>();

  return (
    <Accordion defaultIndex={[0, 1]} allowMultiple>
      <Stack spacing="4">
        <AccordionItem border="0">
        <Stack borderRadius="8px" overflow="hidden" bg="white" boxShadow="sm" border="1px solid #E2E8F0">
          
          <AccordionButton  p="3" bg="secondary.500" roundedTop="lg" _hover={{ bg: 'secondary.500' }}
              _active={{bg:'secondary.500'}}>
            <Box
              bg="secondary.500"
              color="blue.600"
              rounded="full"
              w="6"
              h="6"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="sm"
              fontWeight="bold"
              mr="3"
            >
              1
            </Box>

            <Box flex="1" textAlign="left" fontWeight="semibold">
              Personal Details
            </Box>

            <Stack direction="row">
              <Button variant="outline" bg="transparent" _active={{bg: "transparent"}} _hover={{bg: "transparent"}} h="20px" w="20px" border="0" p="2" onClick={() => setActiveStep(0)}>
                <EditIcon p="0" boxSize={5} color="primary.500" />
              </Button>
                <AccordionIcon color="primary.500"/>
              </Stack>
          </AccordionButton>

          <AccordionPanel pb="6">
            <SimpleGrid columns={3} spacingX="10" spacingY="5">
              <ReviewField label="First Name" value={values.firstName} />
              <ReviewField label="Last Name" value={values.lastName} />
              <ReviewField label="Contact Email" value={values.contactEmail} />
              <ReviewField label="Primary Phone" value={values.primaryPhone} />
              <ReviewField label="Company" value={values.company} />
              <ReviewField label="Job Title" value={values.jobTitle} />
              <ReviewField label="Street Address" value={values.addressLine0} />
              <ReviewField label="City" value={values.addressLocality} />
              <ReviewField
                label="State / Province / Region"
                value={values.addressAdministrativeArea}
              />
              <ReviewField label="Country" value={values.addressRegionCode} />
              <ReviewField label="Postal / Zip Code" value={values.addressPostalCode} />
            </SimpleGrid>
          </AccordionPanel>
          </Stack>
        </AccordionItem>

        <AccordionItem border="0">
        <Stack borderRadius="8px" overflow="hidden" bg="white" boxShadow="sm" border="1px solid #E2E8F0">

          <AccordionButton p="3" bg="secondary.500" roundedTop="lg" _hover={{ bg: 'secondary.500' }}
              _active={{bg:'secondary.500'}}>
            <Box
              bg="secondary.500"
              color="blue.600"
              rounded="full"
              w="6"
              h="6"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="sm"
              fontWeight="bold"
              mr="3"
            >
              2
            </Box>

            <Box flex="1" textAlign="left" fontWeight="semibold">
              Account Details
            </Box>

              <Stack direction="row">
              <Button variant="outline" bg="transparent" _active={{bg: "transparent"}} _hover={{bg: "transparent"}} h="20px" w="20px" border="0" p="2" onClick={() => setActiveStep(1)}>
                <EditIcon p="0" boxSize={5} color="primary.500" />
              </Button>
                <AccordionIcon color="primary.500"/>
              </Stack>
          </AccordionButton>

          <AccordionPanel pb="6">
            <SimpleGrid columns={3} spacingX="10" spacingY="5">
              <ReviewField label="Account Email" value={values.accountEmail} />
              <ReviewField label="Role" value={values.role} />
              <ReviewField
                label="Send Welcome Email"
                value={values.shouldSendInvitationEmail ? 'Active' : 'Inactive'}
              />
            </SimpleGrid>
          </AccordionPanel>
          </Stack>
        </AccordionItem>
      </Stack>
    </Accordion>
  );
};


  const {
    handleSubmit,
    reset,
    setValue,
    getValues,
    getFieldState,
    formState: { isSubmitting },
  } = methods;

  const contactType = useWatch({
    control: methods.control,
    name: 'contactType',
  });

  const firstName = useWatch({
    control: methods.control,
    name: 'firstName',
  });

  const lastName = useWatch({
    control: methods.control,
    name: 'lastName',
  });

  const accountEmail = useWatch({
    control: methods.control,
    name: 'accountEmail',
  });

  const isRecordOnly = useWatch({
    control: methods.control,
    name: 'isRecordOnly',
  });

  const isPersonalDetailsValid = firstName && lastName;
  const isAccountDetailsValid = isRecordOnly || (accountEmail && REG_EXP.EmailWithTLD.test(accountEmail));

  const [createUser, createUserResult] = useMutation(CREATE_USER, {
    update(cache) {
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'organizations' });
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'organizationSearch' });
      cache.gc();
    },
  });

  const [activeStep, setActiveStep] = useState(0);
  const companySubmitRef = useRef<(() => void) | null>(null);
  const [loadingAction, setLoadingAction] = useState<AddContactModalAction | null>(null);

  useEffect(() => {
    if (isOpen) {
      setActiveStep(0);
      reset(defaultValues);
    }
  }, [isOpen, reset, defaultValues]);

  const isLoading = isSubmitting || createUserResult.loading;

  const submitIndividual = async (
    data: AddContactModalValues,
    action: AddContactModalAction
  ) => {
    if (!data.firstName) {
      throw new Error('Unexpected Missing Field: First Name');
    }

    if (!data.lastName) {
      throw new Error('Unexpected Missing Field: Last Name');
    }

    if (data.shouldSendInvitationEmail === undefined) {
      throw new Error('Unexpected Missing Field: Should Send Invitation Email');
    }

    const input: CreateUserV2Input = {
      firstName: data.firstName,
      lastName: data.lastName,
      contactEmail: data.contactEmail || undefined,
      primaryPhone: data.primaryPhone || undefined,
      jobTitle: data.jobTitle || undefined,
      company: data.company || undefined,
      addressLine0: data.addressLine0 || undefined,
      addressLocality: data.addressLocality || undefined,
      addressAdministrativeArea: data.addressAdministrativeArea || undefined,
      addressRegionCode: data.addressRegionCode || undefined,
      addressPostalCode: data.addressPostalCode || undefined,
    };

    // NewUser supports creating either an integration member or non-member.
    // NewUserMemberManagement keeps this undefined and uses membership routing instead.
    if (flow === 'newUser' && (action === 'member' || action === 'nonMember')) {
      input.integrationIsMember = action === 'member';
    }

    if (!data.isRecordOnly) {
      if (!data.accountEmail) {
        throw new Error('Unexpected Missing Field: Account Email');
      }

      input.accountData = {
        email: data.accountEmail,
        emailInvitation: {
          sendEmailInvitation: data.shouldSendInvitationEmail,
        },
        createWithRole: {
          userRole: data.role,
        },
      };

      if (
        data.role === EUserRole.ORGANIZATION_ACCOUNT_OWNER ||
        data.role === EUserRole.ORGANIZATION_PAGE_ADMIN
      ) {
        if (!data.organization) {
          throw new Error('Unexpected Missing Field: Organization');
        }

        if (
          data.organization.id ===
          SEARCHABLE_ORGANIZATION_SELECT_NEW_ORGANIZATION_PLACEHOLDER_ID
        ) {
          input.accountData.createWithRole = {
            ...input.accountData.createWithRole,
            newOrganizationData: {
              name: data.organization.name,
              isSponsor: false,
            },
          };
        } else {
          input.accountData.createWithRole = {
            ...input.accountData.createWithRole,
            organizationId: data.organization.id,
          };
        }
      }
    }

    const result = await createUser({
      variables: { input },
    });

    if (result.data?.createUserV2.__typename === 'CreateUserV2Success') {
      const user = result.data.createUserV2.user;

      toast({
        title: `You successfully added ${user.displayName}!`,
        status: 'success',
      });

      onClose();
      reset(defaultValues);

      if (action === 'continue') {
        router.push(`/admin/membership/people/${user._id}/membership/new`);
      }

      if (action === 'nonMember' || action === 'member') {
        router.push(`/admin/membership/people/${user._id}`);
      }

      return;
    }

    if (
      result.data?.createUserV2.__typename ===
      'CreateUserV2DuplicateEmailError'
    ) {
      toast({
        title: 'A user with that email already exists',
        status: 'error',
      });
      return;
    }

    if (
      result.data?.createUserV2.__typename ===
      'CreateUserV2InvalidInputError'
    ) {
      toast({
        title: result.data.createUserV2.message,
        status: 'error',
      });
      return;
    }

    toast({
      title: 'An unknown error occurred',
      status: 'error',
    });
  };

  const onSubmitInternal = async (
    values: AddContactModalValues,
    action: AddContactModalAction = 'save'
  ) => {
    try {
      if (values.contactType === 'Organization') {
        return;
      }

      await submitIndividual(values, action);
    } catch (error) {
      logError(error);
      toast({
        title: 'An unknown error occurred',
        status: 'error',
      });
    }
  };

  const handleActionSubmit = (action: AddContactModalAction) =>
    handleSubmit((values) => onSubmitInternal(values, action));

  const handleActionClick = (action: AddContactModalAction) => {
    return async (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setLoadingAction(action);
      try {
        await handleActionSubmit(action)(event);
      } finally {
        setLoadingAction(null);
      }
    };
  };

  // const steps = useMemo(
  //   () => [
  //     {
  //       label: 'Personal Details',
  //       content: (
  //         <PersonalDetailsForm
  //           onBlurContactEmail={() => {
  //             if (!getFieldState('accountEmail').isTouched) {
  //               setValue('accountEmail', getValues('contactEmail'));
  //             }
  //           }}
  //           isLoading={isLoading}
  //           twoColumn={true}
  //         />
  //       ),
  //     },
  //     {
  //       label: 'Account Details',
  //       content: <AccountForm isLoading={isLoading} />,
  //     },
  //     {
  //       label: 'Review',
  //       content: <Box />,
  //     },
  //   ],
  //   [getFieldState, getValues, isLoading, setValue]
  // );

  const steps = useMemo(
  () => [
    {
      label: 'Personal Details',
      content: (
        <PersonalDetailsForm
          onBlurContactEmail={() => {
            if (!getFieldState('accountEmail').isTouched) {
              setValue('accountEmail', getValues('contactEmail'));
            }
          }}
          isLoading={isLoading}
          twoColumn={true}
        />
      ),
    },
    {
      label: 'Account Details',
      content: <AccountForm isLoading={isLoading} />,
    },
    {
      label: 'Review',
      content: <ReviewDetails />,
    },
  ],
  [getFieldState, getValues, isLoading, setValue]
);

  const handleNext = async () => {
    // Trigger validation for current step
    let fieldsToValidate: (keyof AddContactModalValues)[] = [];
    
    if (activeStep === 0) {
      fieldsToValidate = ['firstName', 'lastName'];
    } else if (activeStep === 1) {
      fieldsToValidate = ['accountEmail'];
    }
    
    // Trigger validation and get the result
    const isValid = await methods.trigger(fieldsToValidate);
    
    // Also manually check our validation logic
    const currentValues = methods.getValues();
    let canProceed = false;
    
    if (activeStep === 0) {
      canProceed = Boolean(currentValues.firstName && currentValues.lastName);
    } else if (activeStep === 1) {
      canProceed = Boolean(currentValues.isRecordOnly || 
        (currentValues.accountEmail && REG_EXP.EmailWithTLD.test(currentValues.accountEmail)));
    }
    
    // Only proceed if both form validation and our logic pass
    if (isValid && canProceed) {
      setActiveStep((previous) => Math.min(previous + 1, steps.length - 1));
    }
  };

  const handlePrevious = () => {
    setActiveStep((previous) => Math.max(previous - 1, 0));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader borderBottom="1px solid" borderColor="strokePrimary.500">Add Contact</ModalHeader>
        <ModalCloseButton />

        <FormProvider {...methods}>
          <Box>
            <ModalBody maxH="70vh" overflowY="auto" pt="4">
              <Stack spacing="6">
                <FormControl>
                  <FormLabel>Contact Type</FormLabel>
                  <RadioGroup
                    value={contactType}
                    onChange={(value) =>
                      methods.setValue(
                        'contactType',
                        value as 'Individual' | 'Organization'
                      )
                    }
                  >
                    <Stack direction="row" spacing="4">
                      <Radio value="Individual">
                        <Text fontWeight="medium">Individual</Text>
                      </Radio>
                      <Radio value="Organization">
                        <Text fontWeight="medium">Organization</Text>
                      </Radio>
                    </Stack>
                  </RadioGroup>
                </FormControl>

                {contactType === 'Organization' ? (
                  <Stack spacing="6">
                    <CreateCompanyForm
                      modalButton={true}
                      onClose={onClose}
                      twoColumn={true}
                      submitRef={companySubmitRef}
                    />
                  </Stack>
                ) : showStepper ? (
                  <Stack spacing="4" position="relative">
                    <Stack
                      direction="row"
                      spacing="2"
                      align="center"
                      w="100%"
                      justify="space-between"
                      position="relative"
                      zIndex={1}
                    >
                      {steps.map((step, index) => {
                        const isActive = index === activeStep;
                        const isCompleted = index < activeStep;
                        let isDisabled = false;
                        
                        if (index === 1 && !isPersonalDetailsValid) {
                          isDisabled = true;
                        }
                        if (index === 2 && (!isPersonalDetailsValid || !isAccountDetailsValid)) {
                          isDisabled = true;
                        }

                        return (
                          <Button
                            key={step.label}
                            size={{ base: 'xs', md: 'sm' }}
                            variant={
                              isActive || isCompleted ? 'solid' : 'outline'
                            }
                            colorScheme={
                              isActive || isCompleted ? 'primary' : 'gray'
                            }
                            onClick={() => setActiveStep(index)}
                            position="relative"
                            borderColor="primary.500"
                            zIndex={2}
                            fontSize={{ base: 'xs', md: 'sm' }}
                            fontWeight="medium"
                            whiteSpace="nowrap"
                            borderRadius="999px"
                            isDisabled={isDisabled}
                            opacity={isDisabled ? 0.5 : 1}
                            _disabled={{
                              bg: 'white',
                              color: 'gray.500',
                              borderColor: 'primary.300',
                              opacity: 1,
                              cursor: 'not-allowed',
                            }}
                              _hover={
                              isDisabled
                                ? {
                                    bg: 'white',
                                    color: 'gray.500',
                                    borderColor: 'primary.300',
                                  }
                                : undefined
                            }
                          >
                            {index + 1}. {step.label}
                          </Button>
                        );
                      })}
                    </Stack>

                    <Box
                      position="absolute"
                      top={{ base: '-2px', md: '1px' }}
                      left="0"
                      right="0"
                      h="1px"
                      bg="gray.200"
                      transform="translateY(-50%)"
                      zIndex={0}
                    />

                    <Box>{steps[activeStep].content}</Box>
                  </Stack>
                ) : (
                  <Stack spacing="6">
                    <PersonalDetailsForm
                      onBlurContactEmail={() => {
                        if (!getFieldState('accountEmail').isTouched) {
                          setValue('accountEmail', getValues('contactEmail'));
                        }
                      }}
                      isLoading={isLoading}
                      twoColumn={true}
                      oldview={false}
                    />

                    <AccountForm isLoading={isLoading} oldview={false} />
                  </Stack>
                )}
                
              </Stack>
            </ModalBody>

            <ModalFooter>
              {showStepper &&
              contactType === 'Individual' &&
              activeStep < steps.length - 1 ? (
                <Stack
                  direction="row"
                  spacing="3"
                  width="100%"
                  justify="flex-end"
                >
                  <Button
                    variant="outline"
                    type="button"
                    borderColor="primary.500"
                    onClick={activeStep === 0 ? onClose : handlePrevious}
                  >
                    {activeStep === 0 ? "Cancel" : "Previous"}
                  </Button>

                  <Button
                    type="button" 
                    onClick={handleNext} 
                    colorScheme="blue" 
                    w="100px"
                    isDisabled={activeStep === 0 ? !isPersonalDetailsValid : !isAccountDetailsValid}
                  >
                    Next
                  </Button>
                </Stack>
              ) : contactType === 'Organization' ? (
                <Stack
                  direction="row"
                  spacing="3"
                  width="100%"
                  justify="flex-end"
                >
                  <Button type="button" variant="outline" borderColor="primary.500" onClick={onClose}>
                    Cancel
                  </Button>

                  <Button
                    colorScheme="blue"
                    type="button"
                    onClick={() => companySubmitRef.current?.()}
                  >
                    Add Contact
                  </Button>
                </Stack>
              ) : (
                <Stack
                  direction="row"
                  spacing="3"
                  width="100%"
                  justify="flex-end"
                >
                 <Button
                    type="button"
                    variant="outline"
                    borderColor="primary.500"
                    onClick={activeStep === 0 ? onClose : handlePrevious}
                  >
                    {activeStep === 0 ? "Cancel" : "Previous"}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    colorScheme="primary"
                    borderColor="primary.500"
                    onClick={handleActionClick('nonMember')}
                    isDisabled={loadingAction !== null || !isPersonalDetailsValid || !isAccountDetailsValid}
                    isLoading={loadingAction === 'nonMember'}
                  >
                    Add as a Non-Member
                  </Button>

                  {flow === 'newUser' ? (
                    <Button
                      type="button"
                      onClick={handleActionClick('member')}
                      isDisabled={loadingAction !== null || !isPersonalDetailsValid || !isAccountDetailsValid}
                      isLoading={loadingAction === 'member'}
                    >
                      Add as a Member
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={handleActionClick('continue')}
                      isDisabled={loadingAction !== null || !isPersonalDetailsValid || !isAccountDetailsValid}
                      isLoading={loadingAction === 'continue'}
                    >
                      Continue to Membership
                    </Button>
                  )}

                  {/* <Button
                    type="submit"
                    colorScheme="blue"
                    isDisabled={isLoading}
                    isLoading={isLoading}
                  >
                    Save Contact
                  </Button> */}
                </Stack>
              )}
            </ModalFooter>
          </Box>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
};